/**
 * GPS preprocessing for pitch auto-calibration — TypeScript port of the research
 * chain's `experiments/gps_preprocessing.py :: preprocess_gps`. Cleans a raw GPS
 * track (accuracy filter -> outlier removal -> warmup/cooldown trim -> second
 * outlier pass) before it is fed to the PitchNet model.
 *
 * Ported 1:1 including the numpy median / percentile semantics so the cleaned
 * output matches the Python reference (golden-tested). The 60 m outlier radius
 * is validated across ~160 real matches — do NOT tighten it (see the Python
 * docstring / FINDINGS).
 *
 * NOTE: `refsix-core`'s existing `GpsProcessingService` cleans GPS for stats
 * (speed/accuracy percentile, BOLT_CONSTANT), but its pipeline is tuned for
 * per-point speed integrity, not the median-radius pitch-cluster extraction the
 * model was trained on. This is a deliberately separate, model-matched cleaner.
 */

import { GpsPoint } from "refsix-js-models";

export interface CleanedGps {
    lats: number[];
    lngs: number[];
    times: number[];
    originalCount: number;
    cleanedCount: number;
    removedOutliers: number;
    removedAccuracy: number;
    removedWarmup: number;
    isIndoor: boolean;
    qualityScore: number;
}

export interface PreprocessOptions {
    accuracyThreshold?: number;
    outlierRadiusM?: number;
    warmupTrimPct?: number;
    minPoints?: number;
}

const EARTH_R = 6371000;

/** Haversine distance in metres between two lat/lng points. */
function haversineM(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
): number {
    const toRad = Math.PI / 180;
    const dlat = (lat2 - lat1) * toRad;
    const dlng = (lng2 - lng1) * toRad;
    const a =
        Math.sin(dlat / 2) ** 2 +
        Math.cos(lat1 * toRad) *
            Math.cos(lat2 * toRad) *
            Math.sin(dlng / 2) ** 2;
    return EARTH_R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** numpy.median: average of the two middle values for even-length input. */
function median(values: number[]): number {
    const s = [...values].sort((a, b) => a - b);
    const n = s.length;
    const mid = Math.floor(n / 2);
    return n % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

/** numpy.percentile with linear interpolation (the numpy default). q in [0,100]. */
function percentileLinear(values: number[], q: number): number {
    const s = [...values].sort((a, b) => a - b);
    const n = s.length;
    if (n === 0) return 0;
    if (n === 1) return s[0];
    const rank = (q / 100) * (n - 1);
    const lo = Math.floor(rank);
    const hi = Math.ceil(rank);
    if (lo === hi) return s[lo];
    return s[lo] + (s[hi] - s[lo]) * (rank - lo);
}

function count(mask: boolean[]): number {
    let c = 0;
    for (const m of mask) if (m) c++;
    return c;
}

/**
 * Clean a raw GPS track. Returns null if the track is unusable (too few valid
 * points at any stage) — the caller should then fall back to manual calibration.
 * Mirrors `preprocess_gps` defaults exactly.
 */
export function preprocessGps(
    points: (GpsPoint | null | undefined)[],
    opts: PreprocessOptions = {}
): CleanedGps | null {
    const accuracyThreshold = opts.accuracyThreshold ?? 50.0;
    const outlierRadiusM = opts.outlierRadiusM ?? 60.0;
    const warmupTrimPct = opts.warmupTrimPct ?? 0.05;
    const minPoints = opts.minPoints ?? 50;

    if (!points || points.length < minPoints) return null;

    // Keep only points that carry a usable lat/lng. Number.isFinite (not
    // typeof) so NaN/Infinity coordinates are rejected here deterministically
    // instead of poisoning the median/haversine maths downstream.
    const valid = points.filter(
        (p): p is GpsPoint =>
            !!p && Number.isFinite(p.latitude) && Number.isFinite(p.longitude)
    );
    if (valid.length < minPoints) return null;

    const originalCount = valid.length;
    const lats = valid.map((p) => p.latitude);
    const lngs = valid.map((p) => p.longitude);
    const times = valid.map((p) => (typeof p.time === "number" ? p.time : 0));
    const accuracies = valid.map((p) =>
        typeof p.accuracy === "number" ? p.accuracy : 0
    );
    const n = valid.length;

    let removedAccuracy = 0;
    let removedOutliers = 0;
    let removedWarmup = 0;

    // ---- Step 1: filter by GPS accuracy (accuracy <= 0 means "no data", keep) ----
    let mask = accuracies.map((acc) => !(acc > 0) || acc <= accuracyThreshold);
    removedAccuracy = count(mask.map((m) => !m));

    // ---- Step 2: remove extreme outliers vs the median position ----
    if (count(mask) < minPoints) {
        mask = Array.from({ length: n }, () => true); // accuracy filter too aggressive; relax
        removedAccuracy = 0;
    }

    const maskedLats = lats.filter((_, i) => mask[i]);
    const maskedLngs = lngs.filter((_, i) => mask[i]);
    const medLat = median(maskedLats);
    const medLng = median(maskedLngs);

    const distances = lats.map((lat, i) =>
        haversineM(lat, lngs[i], medLat, medLng)
    );
    const withinRadius = distances.map((d) => d <= outlierRadiusM);
    removedOutliers = count(mask.map((m, i) => m && !withinRadius[i]));
    mask = mask.map((m, i) => m && withinRadius[i]);

    if (count(mask) < minPoints) return null;

    // ---- Step 3: trim warmup / cooldown ----
    let indices: number[] = [];
    for (let i = 0; i < n; i++) if (mask[i]) indices.push(i);

    if (times[indices[0]] > 0) {
        // Sort masked indices by time, trim first/last N%.
        const timeSorted = [...indices].sort((i, j) => times[i] - times[j]);
        const nTrim = Math.max(
            1,
            Math.trunc(timeSorted.length * warmupTrimPct)
        );
        const trimmed = timeSorted.slice(nTrim, timeSorted.length - nTrim);
        removedWarmup = timeSorted.length - trimmed.length;
        mask = Array.from({ length: n }, () => false);
        for (const i of trimmed) mask[i] = true;
    } else {
        // No timestamps — trim by index order.
        const nTrim = Math.max(1, Math.trunc(indices.length * warmupTrimPct));
        const trimmed = indices.slice(nTrim, indices.length - nTrim);
        removedWarmup = indices.length - trimmed.length;
        mask = Array.from({ length: n }, () => false);
        for (const i of trimmed) mask[i] = true;
    }

    if (count(mask) < minPoints) return null;

    // ---- Step 4: second, tighter outlier pass on the cleaned data ----
    const cleanLats = lats.filter((_, i) => mask[i]);
    const cleanLngs = lngs.filter((_, i) => mask[i]);
    const medLat2 = median(cleanLats);
    const medLng2 = median(cleanLngs);

    const distances2 = lats.map((lat, i) =>
        haversineM(lat, lngs[i], medLat2, medLng2)
    );
    const cleanDists = distances2.filter((_, i) => mask[i]);
    const q75 = percentileLinear(cleanDists, 75);
    const tightRadius = Math.max(50, Math.min(outlierRadiusM, q75 * 3));

    let finalMask = mask.map((m, i) => m && distances2[i] <= tightRadius);

    if (count(finalMask) < minPoints) {
        finalMask = mask; // fall back to pre-tightening
    } else {
        // Only count the tight pass's removals when it was actually kept.
        removedOutliers += count(mask.map((m, i) => m && !finalMask[i]));
    }

    // ---- Quality assessment ----
    const outLats: number[] = [];
    const outLngs: number[] = [];
    const outTimes: number[] = [];
    const outAcc: number[] = [];
    for (let i = 0; i < n; i++) {
        if (finalMask[i]) {
            outLats.push(lats[i]);
            outLngs.push(lngs[i]);
            outTimes.push(times[i]);
            outAcc.push(accuracies[i]);
        }
    }

    const validAcc = outAcc.filter((a) => a > 0);
    let isIndoor = false;
    if (validAcc.length > 10) {
        const meanAcc = validAcc.reduce((s, a) => s + a, 0) / validAcc.length;
        isIndoor = meanAcc > 30;
    }

    const retention = count(finalMask) / originalCount;
    const meanValidAcc =
        validAcc.length > 0
            ? validAcc.reduce((s, a) => s + a, 0) / validAcc.length
            : 0;
    const accScore =
        validAcc.length > 0 ? 1.0 - Math.min(1.0, meanValidAcc / 50.0) : 0.5;
    const qualityScore = 0.5 * retention + 0.5 * accScore;

    return {
        lats: outLats,
        lngs: outLngs,
        times: outTimes,
        originalCount,
        cleanedCount: count(finalMask),
        removedOutliers,
        removedAccuracy,
        removedWarmup,
        isIndoor,
        qualityScore,
    };
}
